// ============================================================================
// Bases y Propiedades — store de bases/locaciones, personal y resumen
// ============================================================================
// Lee y escribe por la API (`property_bases`, `property_base_staff`, por
// compañía en el servidor; CC-CONSOLE #1592). Antes era una maqueta en
// localStorage con coworkings de ejemplo; ahora los ejemplos salen del Sandbox.
// ============================================================================

import { create } from "zustand";
import * as basesApi from "./services/basesApi";
import type { Base, BaseStaff, BasesSummary } from "./services/basesApi";
import type { LoadState } from "./store";

export type { Base, BaseStaff, BasesSummary, BaseType, BaseStatus, BaseZone } from "./services/basesApi";
export { BASE_TYPES, BASE_STATUSES, STAFF_ROLES, STAFF_STATUSES } from "./services/basesApi";

interface BasesLocacionesState {
  bases: Base[];
  staff: BaseStaff[];
  summary: BasesSummary | null;
  status: LoadState;
  error: string | null;
  hydrate: () => Promise<void>;
  refreshSummary: () => Promise<void>;
  addBase: (draft: Partial<Omit<Base, "id">>, files?: File[]) => Promise<Base>;
  updateBase: (id: string, draft: Partial<Omit<Base, "id">>, files?: File[]) => Promise<Base>;
  removeBase: (id: string) => Promise<void>;
  addStaff: (draft: Omit<BaseStaff, "id">) => Promise<BaseStaff>;
  updateStaff: (id: string, patch: Partial<Omit<BaseStaff, "id">>) => Promise<BaseStaff>;
  removeStaff: (id: string) => Promise<void>;
}

const errorText = (err: unknown) => basesApi.apiErrorMessage(err, (err as Error)?.message || "error");

export const useBasesLocacionesStore = create<BasesLocacionesState>((set, get) => ({
  bases: [],
  staff: [],
  summary: null,
  status: "idle",
  error: null,

  hydrate: async () => {
    set({ status: get().status === "ready" ? "ready" : "loading", error: null });
    try {
      const [bases, staff, summary] = await Promise.all([
        basesApi.listBases(),
        basesApi.listStaff(),
        basesApi.getSummary().catch(() => null),
      ]);
      set({ bases, staff, summary, status: "ready" });
    } catch (err) {
      set({ status: "error", error: errorText(err) });
    }
  },

  refreshSummary: async () => {
    try {
      set({ summary: await basesApi.getSummary() });
    } catch {
      /* el resumen es opcional */
    }
  },

  addBase: async (draft, files = []) => {
    const created = await basesApi.createBase(draft, files);
    set({ bases: [...get().bases, { ...created, staffCount: 0 }].sort((a, b) => a.name.localeCompare(b.name, "es")) });
    void get().refreshSummary();
    return created;
  },

  updateBase: async (id, draft, files = []) => {
    const saved = await basesApi.updateBase(id, draft, files);
    set({ bases: get().bases.map((b) => (b.id === id ? { ...saved, staffCount: b.staffCount } : b)) });
    void get().refreshSummary();
    return saved;
  },

  removeBase: async (id) => {
    await basesApi.deleteBase(id);
    set({ bases: get().bases.filter((b) => b.id !== id) });
    void get().refreshSummary();
  },

  addStaff: async (draft) => {
    const created = await basesApi.createStaff(draft);
    set({
      staff: [...get().staff, created],
      bases: get().bases.map((b) => (b.id === created.base_id ? { ...b, staffCount: (b.staffCount ?? 0) + 1 } : b)),
    });
    return created;
  },

  updateStaff: async (id, patch) => {
    const saved = await basesApi.updateStaff(id, patch);
    const staff = get().staff.map((s) => (s.id === id ? saved : s));
    const count: Record<string, number> = {};
    for (const s of staff) count[s.base_id] = (count[s.base_id] ?? 0) + 1;
    set({ staff, bases: get().bases.map((b) => ({ ...b, staffCount: count[b.id] ?? 0 })) });
    return saved;
  },

  removeStaff: async (id) => {
    const person = get().staff.find((s) => s.id === id);
    await basesApi.deleteStaff(id);
    set({
      staff: get().staff.filter((s) => s.id !== id),
      bases: get().bases.map((b) => (person && b.id === person.base_id ? { ...b, staffCount: Math.max(0, (b.staffCount ?? 1) - 1) } : b)),
    });
  },
}));
